import { readStoredToken } from './authApi';

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').trim();

if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL must be configured for the frontend build');
}

function getAuthToken() {
  return readStoredToken();
}

function authHeaders(includeJson = false) {
  const headers = {};
  if (includeJson) headers['Content-Type'] = 'application/json';
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function parseJson(res) {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

async function request(path, options = {}) {
  let res;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: { ...authHeaders(), ...(options.headers || {}) },
    });
  } catch {
    throw new Error('Could not reach the community service.');
  }

  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.detail || data.error || `Request failed (${res.status})`);
  }
  return data;
}

async function issueChatToken(roomId = 'all') {
  return request(`/community/chats/${encodeURIComponent(roomId)}/token`, {
    method: 'POST',
    headers: authHeaders(true),
  });
}

async function listPinnedMessages(roomId = 'all') {
  return request(`/community/chats/${encodeURIComponent(roomId)}/pinned`);
}

async function listMembers(roomId = 'all', { limit = 200 } = {}) {
  const query = `?limit=${encodeURIComponent(limit)}`;
  return request(`/community/chats/${encodeURIComponent(roomId)}/members${query}`);
}

async function fetchHistory(roomId = 'all', { limit = 50, lookbackHours = 2160 } = {}) {
  const query = `?limit=${encodeURIComponent(limit)}&lookback_hours=${encodeURIComponent(lookbackHours)}`;
  return request(`/community/chats/${encodeURIComponent(roomId)}/history${query}`);
}

async function pinMessage(roomId, { messageId, content, senderUserId, senderDisplayName }) {
  return request(`/community/chats/${encodeURIComponent(roomId)}/pinned`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ messageId, content, senderUserId, senderDisplayName }),
  });
}

async function unpinMessage(roomId, messageId) {
  return request(
    `/community/chats/${encodeURIComponent(roomId)}/pinned/${encodeURIComponent(messageId)}`,
    { method: 'DELETE' },
  );
}

async function listChats() {
  return request('/community/chats');
}

async function leaveUserChat(ownerUsername) {
  return request(
    `/community/chats/user/${encodeURIComponent(ownerUsername)}/leave`,
    { method: 'POST' },
  );
}

async function joinUserChat(ownerUsername) {
  return request(
    `/community/chats/user/${encodeURIComponent(ownerUsername)}/join`,
    { method: 'POST' },
  );
}

const communityApi = {
  issueChatToken,
  listPinnedMessages,
  listMembers,
  fetchHistory,
  pinMessage,
  unpinMessage,
  listChats,
  leaveUserChat,
  joinUserChat,
};

export default communityApi;
